import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, FileText, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import { downloadTemplate } from './oimTemplates';

/**
 * OIM CSV uploader.
 *
 * Props:
 *   fileType: 'energy' | 'production' | 'cost'
 *   siteId:   REQUIRED — the active site selected via SiteSelector. The backend
 *             stamps every row with this site_id, so it does NOT need to appear
 *             in the CSV. Upload is disabled until a site is selected.
 *   onUploaded: callback fired with the ingestion result, used by parent to refresh
 */

const FILE_TYPE_META = {
  energy: {
    label: 'Energy (hourly)',
    columns: 'timestamp, energy_type, energy_value, unit, [source], [quality_flag], [notes]',
    // Use each meter's natural unit. Backend converts to kWh-equivalent before
    // computing intensity. Recognized energy_type values:
    //   carriers: electricity, steam, renewable
    //   steam split: steam_generated (boiler outlet), steam_consumed (process inlet)
    //                — opt in to populate Steam Losses on the Steam dashboard
    //   liquid:   diesel, petrol, furnace_oil, fuel
    //   gaseous:  gas (natural_gas), lpg
    //   solid:    coal, bagasse, husk, rice_husk, biomass, wood, spent_wash
    //   biogenic concentrate: spent_wash (vinasse_concentrate alias) — typical
    //                         for ZLD distilleries co-firing MEE concentrate
    example: 'electricity (kWh), steam_generated/steam_consumed (tonne), bagasse/rice_husk/spent_wash (tonne), diesel/petrol (litre), gas (m3) …',
  },
  production: {
    label: 'Production (hourly)',
    columns: 'timestamp, product, production_value, unit, [source], [estimation_flag], [notes]',
    example: '2026-04-29T10:00:00Z, ethanol, 52.3, kL, MES, false',
  },
  cost: {
    label: 'Cost (daily)',
    columns: 'date, product, total_cost, [energy_cost_per_unit], [raw_material_cost], [currency], [is_actual]',
    example: '2026-04-29, ethanol, 50000, 6.5, 32000, INR, false',
  },
  water: {
    label: 'Water (hourly)',
    columns: 'timestamp, water_type, water_value, unit, [source], [quality_flag], [notes]',
    // water_type: raw / treated / recycled / cooling / process / wastewater /
    //             potable / condensate (boiler condensate return — populates
    //             Condensate Recovery on the Steam dashboard) /
    //             zld_recovered (MEE+RO output — populates ZLD Recovery %)
    // Units: kL (or m³, equivalent) / litre. Backend normalizes all to kL.
    example: '2026-04-29T10:00:00Z, process, 350.0, kL, meter, raw',
  },
  waste: {
    label: 'Waste (hourly)',
    columns: 'timestamp, waste_type, waste_value, unit, [is_hazardous], [disposal_method], [source], [notes]',
    // waste_type: vinasse / spent_wash / effluent / wastewater (liquid) | ash / sludge / solid (solid)
    //             zld_input (vinasse fed to MEE+RO — denominator for ZLD Recovery %)
    // Liquid in kL, solid in tonne. Keep one stream consistent — mixed units flagged at compute time.
    example: '2026-04-29T10:00:00Z, vinasse, 520.0, kL, false, etp_treatment, plant',
  },
};

const OimUpload = ({ fileType, siteId, onUploaded }) => {
  const meta = FILE_TYPE_META[fileType] || FILE_TYPE_META.energy;
  const inputRef = useRef(null);
  const { showSuccess, showError } = useAlert();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handlePick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      showError('Only .csv files are accepted');
      return;
    }
    setFile(f);
    setLastResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!siteId) {
      showError('Select a site before uploading — every row will be attributed to that site');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const qs = `?site_id=${encodeURIComponent(siteId)}`;
      const response = await axios.post(`${API}/oim/ingest/${fileType}${qs}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data && response.data.data;
      setLastResult(data);
      if (data && data.rows_failed === 0) {
        showSuccess(`Ingested ${data.rows_inserted} of ${data.rows_total} rows`);
      } else if (data) {
        showSuccess(
          `Ingested ${data.rows_inserted} of ${data.rows_total} rows (${data.rows_failed} failed)`
        );
      }
      if (onUploaded) onUploaded(data);
    } catch (err) {
      console.error('OIM upload failed', err);
      const msg = (err.response && err.response.data && err.response.data.detail) || 'Upload failed';
      showError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadTemplate(fileType);
      showSuccess('Template downloaded — site is applied at upload time from the site selector');
    } catch (e) {
      console.error('OIM template download failed', e);
      showError('Failed to generate template');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          <h4 className="font-semibold text-gray-900">{meta.label}</h4>
        </div>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
          title="Download a sample CSV with your active site_id pre-filled"
        >
          <Download className="w-3.5 h-3.5" />
          Download template
        </button>
      </div>
      <div className="text-xs text-gray-600 mb-1">
        <span className="font-medium">Required columns:</span> {meta.columns}
      </div>
      <div className="text-xs text-gray-500 mb-3 font-mono">
        e.g. {meta.example}
      </div>

      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handlePick}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md text-sm font-medium border border-gray-300"
        >
          Choose CSV
        </button>
        <span className="text-xs text-gray-600 truncate max-w-xs">
          {file ? file.name : 'No file selected'}
        </span>
        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || uploading || !siteId}
          title={!siteId ? 'Select a site first' : undefined}
          className="ml-auto px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium inline-flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      {!siteId && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Select a site from the top selector before uploading. Every row will be attributed to that site.
        </div>
      )}

      {lastResult && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md text-xs">
          <div className="flex items-center gap-2 mb-1">
            {lastResult.rows_failed === 0 ? (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600" />
            )}
            <span className="font-semibold">
              {lastResult.rows_inserted} inserted · {lastResult.rows_failed} failed · {lastResult.rows_total} total
            </span>
          </div>
          {lastResult.errors && lastResult.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-amber-700 font-medium">
                {lastResult.errors.length} error(s) — click to expand
              </summary>
              <ul className="mt-1 ml-4 space-y-0.5 list-disc">
                {lastResult.errors.slice(0, 10).map((e, i) => (
                  <li key={i} className="text-gray-700">
                    Row {e.row}: {e.message}
                  </li>
                ))}
                {lastResult.errors.length > 10 && (
                  <li className="text-gray-500">… and {lastResult.errors.length - 10} more</li>
                )}
              </ul>
            </details>
          )}
          {lastResult.flags && lastResult.flags.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-blue-700 font-medium">
                {lastResult.flags.length} flag(s)
              </summary>
              <ul className="mt-1 ml-4 space-y-0.5 list-disc">
                {lastResult.flags.slice(0, 10).map((f, i) => (
                  <li key={i} className="text-gray-700">
                    Row {f.row}: <code className="text-blue-700">{f.flag}</code> — {f.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default OimUpload;
